import { useIntl } from 'react-intl';
import {
    RenderableVolume,
    TileAlignment,
    TileGeometryLink,
} from '../adapters/types';
import { useChangeVersion } from '../adapters/useChangeVersion';
import { ImageLayer, Plane, ViewportCell } from '../layout/types';
import SecondaryImagePlaceholder from '../tile/SecondaryImagePlaceholder';
import Tile from '../tile/Tile';
import { PlaneCycleControl } from '../layout/planeCycle';
import {
    resolvePaneHistograms,
    resolveRowFrame,
    RowBlendControls,
    tileFusionOf,
} from '../tile/tileLink';
import {
    paneAccessibleName,
    paneNamesItsImage,
    imagesDrawnByPane,
    seriesNumberText,
    fusedImageNamedByPane,
    shownImageOfPane,
} from '../tile/tileViewModel';
import DraggableCell from './DraggableCell';
import { imagesLedByPane } from './imagesLedByPane';
import { RowHost } from './RowHost';

interface DraggableTileCellProps {
    cell: ViewportCell;
    plane: Plane;
    /** Which of the row's fused pair the pane draws. */
    layer: ImageLayer;
    volume: RenderableVolume;
    /**
     * Present when the pane's row is locked to the primary row: the pane
     * is a view of the primary image showing the row's image alone.
     */
    alignment: TileAlignment | undefined;
    /**
     * Present when the pane's row is independent but registered to the
     * primary row: the pane draws the primary's structures, read-only.
     */
    structuresThrough: TileAlignment | undefined;
    /** The pane's part in keeping panes of its image at one scale and centre. */
    geometryLink: TileGeometryLink | undefined;
    /** The row's blend controls, so a secondary pane knows whether it has one. */
    blend: RowBlendControls;
    /** The pane's 0-based address, for its accessible name and its drop rule. */
    rowIndex: number;
    columnIndex: number;
    /** Whether this is the row's first image pane: the one that stands for it. */
    leadsRow: boolean;
    /** What each image pane before this one in the row's reading order draws. */
    earlierLayers: ReadonlyArray<ImageLayer>;
    /** The row's plane control, on the pane that offers it. */
    planeCycle?: PlaneCycleControl;
    host: RowHost;
}

/**
 * One image pane of a bound row: the tile, draggable by its title bar to
 * reorder within the row, and a drop target for its row's other panes. A
 * pane asked to draw a secondary the row does not have shows the
 * placeholder that offers the picker instead.
 */
export default function DraggableTileCell({
    cell,
    plane,
    layer: rowLayer,
    volume,
    alignment,
    structuresThrough,
    geometryLink,
    blend,
    rowIndex,
    columnIndex,
    leadsRow,
    earlierLayers,
    planeCycle,
    host,
}: DraggableTileCellProps) {
    useChangeVersion(blend.source);
    const intl = useIntl();
    const renderer = host.rendererFor(cell.id);
    // A locked row shows its own image alone, whatever its pair.
    const layer = alignment ? ImageLayer.Primary : rowLayer;
    const fused = alignment ? undefined : blend.source.getCurrent();
    // A locked pane is a view of the primary image: its frame and its
    // structures are the primary's.
    const viewedVolume =
        (alignment && host.datasetAdapter.resolve(alignment.anchorSeriesUid)) ||
        volume;
    const shownImage = shownImageOfPane(layer, volume.metadata, fused);
    if (!shownImage) {
        return (
            <SecondaryImagePlaceholder
                source={blend.source}
                onFuse={blend.onFuse}
                onClear={blend.onClear}
            />
        );
    }
    const drawnImages = imagesDrawnByPane(
        layer,
        volume.metadata,
        fused,
        (seriesNumber) => seriesNumberText(intl, seriesNumber)
    );
    return (
        <DraggableCell
            cell={cell}
            name={paneAccessibleName(intl, shownImage, plane)}
            rowIndex={rowIndex}
            columnIndex={columnIndex}
            host={host}
        >
            {(dragHandleProps) => (
                <Tile
                    volume={volume}
                    plane={plane}
                    layer={layer}
                    alignment={alignment}
                    structuresThrough={structuresThrough}
                    geometryLink={geometryLink}
                    fusion={tileFusionOf(blend)}
                    shownImage={shownImage}
                    fusedImage={fusedImageNamedByPane(layer, fused)}
                    namesItsImage={paneNamesItsImage(layer, leadsRow)}
                    listedImages={host.binding.availableDatasets}
                    renderer={renderer}
                    frame={resolveRowFrame(
                        viewedVolume.metadata.seriesInstanceUid,
                        host.environmentAdapter
                    )}
                    isActive={host.activeId === cell.id}
                    onSelectRoi={host.roiSelectionFor(viewedVolume)}
                    structureActions={host.structureActionsFor(viewedVolume)}
                    overlay={host.overlayFor(
                        renderer,
                        imagesLedByPane(
                            layer,
                            earlierLayers,
                            volume.metadata,
                            fused
                        )
                    )}
                    status={host.statusFor({ images: drawnImages, leadsRow })}
                    imageDetails={host.imageDetails}
                    drawnImages={drawnImages}
                    onMaximize={() => host.onToggleMaximize(cell.id)}
                    planeCycle={planeCycle}
                    scaleBarVisible={host.scaleBarVisible}
                    windowHistograms={resolvePaneHistograms(
                        drawnImages,
                        volume.metadata,
                        fused,
                        blend.source.getBlend(),
                        host.windowingAdapter
                    )}
                    dragHandleProps={dragHandleProps}
                />
            )}
        </DraggableCell>
    );
}
